import { FC, PropsWithChildren as PWC, useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { gql } from '@apollo/client'
import { isNil } from 'ramda'
import { useFlags } from 'launchdarkly-react-client-sdk'

import Button from '~/src/components/generic/Button'

import SettingsMenu from '~/src/components/shared/NavBar/components/SettingsMenu'
import { ActionEnum } from '~/src/components/shared/NavBar/types'
import UserProfileIcon from '~/src/components/shared/UserProfileIcon'
import NotificationMenu from '~/src/components/shared/NavBar/components/NotificationMenu'
import SupportMenu from '~/src/components/shared/NavBar/components/SupportMenu'
import LanguageMenu from '~/src/components/shared/NavBar/components/LanguageMenu'
import { getNotificationTimeFloor } from '~/src/components/shared/features/helpers'
import useGetNotificationsForUser from '~/src/components/shared/Notification/hooks/useGetNotificationsForUser'
import useSubscribeNotifications from '~/src/components/shared/Notification/hooks/useSubscribeNotifications'
import DynamicLogo from '~/src/components/shared/NavBar/components/DynamicLogo'

import { isRadiusUser } from '~/helpers/auth'

import { useAuthenticatedUser } from '~/hooks/useAuthentication'
import { useTranslation } from '~/src/hooks/useTranslation'

import { tw } from '~/lib/utils'
import ReportIssue from '~/src/components/ReportIssue'
import { MixpanelEvent, resetMixpanel, track } from '~/clients/mixpanelClient'

//TODO: move it to separate file
export const LOGOUT = gql`
  mutation Logout {
    logout {
      id
    }
  }
`
type Props = {
  onOpenSettings?: () => void
  title?: string
  logo?: string
  logoHref?: string
  className?: string
}
const NavBar: FC<PWC<Props>> = ({
  title,
  logo,
  logoHref,
  children,
  className,
  onOpenSettings,
}) => {
  const [isReportIssueOpen, setOpenReportIssue] = useState(false)
  const [user, logout] = useAuthenticatedUser()
  const { i18n } = useTranslation()

  useSubscribeNotifications()

  const { showNotifications, showNewSupportFlow, showLanguageSelector } = useFlags()
  const shouldShowUserConfig = isRadiusUser(user)
  const shouldShowLanguageSelector = showLanguageSelector && !isRadiusUser(user)
  const router = useRouter()

  useEffect(() => {
    if (isNil(user.settings.updatedAt)) {
      onOpenSettings?.()
    }
  }, [user, onOpenSettings])

  const toggleIsReportIssueOpen = () => {
    setOpenReportIssue(!isReportIssueOpen)
  }

  const handleOptionSelect = (action: ActionEnum) => {
    switch (action) {
      case ActionEnum.ReportIssue:
        toggleIsReportIssueOpen()
        break
      case ActionEnum.Settings:
        onOpenSettings?.()
        break
      case ActionEnum.Logout:
        logout({
          onCompleted: (data) => {
            track(MixpanelEvent.UserLogout, { userId: data.logout.id })
            resetMixpanel()
            router.push('/login')
          },
        })
        break
      default:
        break
    }
  }

  const timeFloor = useMemo(() => getNotificationTimeFloor(), [])
  const shouldQueryForNotifications = showNotifications && isRadiusUser(user)

  useGetNotificationsForUser(timeFloor, shouldQueryForNotifications)

  const reportIssueCTA = (
    <Button
      className="text-body-s-emphasized text-default hover:bg-gray-100"
      variant="link"
      onClick={toggleIsReportIssueOpen}
    >
      Report an issue
    </Button>
  )

  return (
    <div
      className={tw(
        'flex min-h-[60px] w-full items-center justify-between border-b border-solid border-gray-200 bg-white px-10 [grid-area:navbar]',
        className
      )}
      data-testid="navbar"
    >
      {isReportIssueOpen && (
        <ReportIssue
          isOpen={isReportIssueOpen}
          isRadiusUser={isRadiusUser(user)}
          isV2={showNewSupportFlow}
          onCloseHandler={toggleIsReportIssueOpen}
        />
      )}
      <div className="flex items-center space-x-2 uppercase tracking-wide">
        {logo && (
          <>
            {logoHref ? (
              <Link href={logoHref}>
                <DynamicLogo imgName={logo} />
              </Link>
            ) : (
              <DynamicLogo imgName={logo} />
            )}
          </>
        )}
        {title && !logo && <div className="text-default">{title}</div>}
        {children}
      </div>
      <div className="flex flex-row gap-2">
        {shouldShowLanguageSelector && <LanguageMenu i18n={i18n} />}
        {showNewSupportFlow ? (
          <SupportMenu onProvideFeedbackClicked={toggleIsReportIssueOpen} />
        ) : (
          reportIssueCTA
        )}
        {shouldShowUserConfig ? (
          <div className="flex items-center gap-4">
            {showNotifications && (
              <div className="w-11 rounded hover:bg-gray-100 active:border-gray-200">
                <NotificationMenu onOpenSettings={onOpenSettings} />
              </div>
            )}
            <div className="flex items-center rounded-full hover:bg-gray-300">
              <UserProfileIcon profilePictureUrl={user?.oauthProfilePictureUrl} />

              <SettingsMenu onOptionSelect={handleOptionSelect} />
            </div>
          </div>
        ) : (
          <UserProfileIcon profilePictureUrl={user?.oauthProfilePictureUrl} />
        )}
      </div>
    </div>
  )
}

export default NavBar
